
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ImageUploader } from '@/components/ui/image-uploader';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { ShortContentInput, ShortContentItem } from '@/hooks/useShortContent';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { RichTextEditor } from '@/components/ui/rich-text-editor/RichTextEditor';

// Form schema
const formSchema = z.object({
  title: z.string().min(3, { message: "Title must be at least 3 characters long" }),
  content: z.string().min(1, { message: "Content is required" }),
  content_type: z.enum(['tip', 'fact', 'highlight', 'quote', 'snippet']),
  image_url: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  status: z.enum(['draft', 'published', 'archived', 'scheduled']),
  is_featured: z.boolean().default(false),
  publish_date: z.date().nullable().optional(),
  display_order: z.number().int().nullable().optional(),
  slug: z.string().nullable().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ShortContentFormProps {
  initialData?: Partial<ShortContentItem>;
  onSubmit: (values: ShortContentInput) => Promise<void>;
  isLoading?: boolean;
}

export function ShortContentForm({
  initialData,
  onSubmit,
  isLoading = false
}: ShortContentFormProps) {
  const [showScheduleOptions, setShowScheduleOptions] = useState(initialData?.status === 'scheduled');
  
  // Convert date string to Date object if it exists
  const defaultValues = {
    ...initialData,
    publish_date: initialData?.publish_date ? new Date(initialData.publish_date) : null,
  };
  
  // Set up form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues as FormValues,
  });
  
  // Watch status to show/hide schedule options
  const watchStatus = form.watch('status');
  React.useEffect(() => {
    setShowScheduleOptions(watchStatus === 'scheduled');
  }, [watchStatus]);
  
  // Handle image upload
  const handleImageUpload = (url: string) => {
    form.setValue('image_url', url);
  };
  
  // Handle form submission
  const handleSubmit = async (values: FormValues) => {
    try {
      // Convert form values to match the ShortContentInput interface
      const submissionData: ShortContentInput = {
        ...values,
        // Ensure required fields are present
        title: values.title,
        content: values.content,
        content_type: values.content_type,
        status: values.status,
        is_featured: values.is_featured,
        // Date object will be handled by the hook
        publish_date: values.publish_date
      };
      
      await onSubmit(submissionData);
      // Don't reset form - user might want to make more changes
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        <div className="grid gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Enter a title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="content_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Content Type</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select content type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="tip">Tip</SelectItem>
                    <SelectItem value="fact">Fact</SelectItem>
                    <SelectItem value="highlight">Highlight</SelectItem>
                    <SelectItem value="quote">Quote</SelectItem>
                    <SelectItem value="snippet">Snippet</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>
                  This determines how the content will be displayed
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Content</FormLabel>
              <FormControl>
                <RichTextEditor
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Write your content here..."
                  className="min-h-[200px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="image_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Featured Image</FormLabel>
              <FormControl>
                <ImageUploader
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Category</FormLabel>
                <FormControl>
                  <Input placeholder="Enter a category" {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="source"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Source</FormLabel>
                <FormControl>
                  <Input placeholder="Content source (optional)" {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="grid gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="display_order"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Display Order</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    placeholder="Display order (optional)" 
                    {...field} 
                    value={field.value === null ? '' : field.value}
                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                  />
                </FormControl>
                <FormDescription>
                  Lower numbers will appear first
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        {showScheduleOptions && (
          <FormField
            control={form.control}
            name="publish_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Publish Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={`w-[240px] pl-3 text-left font-normal ${
                          !field.value && "text-muted-foreground"
                        }`}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value || undefined}
                      onSelect={field.onChange}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormDescription>
                  Content will be automatically published on this date
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        
        <FormField
          control={form.control}
          name="is_featured"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  Featured Content
                </FormLabel>
                <FormDescription>
                  Featured content will appear in prominent positions
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="slug"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Custom URL Slug (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="custom-url-slug" {...field} value={field.value || ''} />
              </FormControl>
              <FormDescription>
                Leave blank to auto-generate from title
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : initialData?.id ? 'Update Content' : 'Create Content'}
        </Button>
      </form>
    </Form>
  );
}
